import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/services/api';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import LoadingSpinner from '@/shared/ui/feedback/LoadingSpinner';
import ErrorMessage from '@/shared/ui/feedback/ErrorMessage';
import PublicLayout from '@/shared/ui/layout/PublicLayout';
import PageLayout from '@/shared/ui/layout/PageLayout';
import TitleWithSelector from '@/shared/ui/layout/TitleWithSelector';
import './styles.css';

interface PublicTestDTO {
  _id: string;
  name: string;
  priority: number;
}

interface PerformanceDetail {
  score: number;
  rank: number;
}

interface ModelPerformanceDTO {
  modelName: string;
  evaluationId: string;
  evaluationDate: string;
  categories: {
    [category: string]: PerformanceDetail;
  };
  color?: string; // Additional field for UI
}

interface PublicResultsDTO {
  testName: string;
  allCategories: string[];
  performanceData: ModelPerformanceDTO[];
}

const ResultsPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [tests, setTests] = useState<PublicTestDTO[]>([]);
  const [selectedTest, setSelectedTest] = useState<string>('');
  const [results, setResults] = useState<PublicResultsDTO | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('Overall');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTests = async () => {
    try {
      setLoading(true);
      const testsData = await api.public.getPublicTests();
      setTests(testsData);
      if (testsData.length > 0 && !selectedTest) {
        setSelectedTest(testsData[0]._id);
      } else if (testsData.length === 0) {
        setLoading(false);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch tests');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTests();
  }, []);

  useEffect(() => {
    const fetchResults = async (testId: string) => {
      try {
        setLoading(true);
        const resultsData: PublicResultsDTO = await api.public.getPublicResults(testId);

        setResults(resultsData);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch results');
        setResults(null);
      } finally {
        setLoading(false);
      }
    };

    if (selectedTest) {
        setSelectedCategory('Overall');
        fetchResults(selectedTest);
    }
  }, [selectedTest]);


  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
  };

  const modelPerformances = results?.performanceData
    .map(p => ({
      modelName: p.modelName,
      evaluationId: p.evaluationId,
      evaluationDate: new Date(p.evaluationDate),
      score: p.categories[selectedCategory]?.score ?? 0,
      rank: p.categories[selectedCategory]?.rank ?? 0,
      color: p.color,
    }))
    .sort((a, b) => a.rank - b.rank) // Sort by rank for the selected category
    ?? [];
  
  if (loading && !results) {
    return (
      <PublicLayout>
        <LoadingSpinner message="Loading results..." />
      </PublicLayout>
    );
  }
  
  if (error) {
    return (
      <PublicLayout>
        <ErrorMessage message={error} onRetry={() => { setSelectedTest(''); fetchTests(); }} />
      </PublicLayout>
    );
  }

  if (tests.length === 0 && !loading) {
    return (
      <PublicLayout>
        <PageLayout title="Leaderboard">
          <div className="no-data"><p>No public tests available yet.</p></div>
        </PageLayout>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <PageLayout
        title={
          <TitleWithSelector
            title="Leaderboard"
            options={tests.map(t => ({ value: t._id, label: t.name }))}
            selectedValue={selectedTest}
            onChange={setSelectedTest}
          />
        }
        headerContent={results && (
          <>
            <div className="category-tabs">
              {results.allCategories.map(category => (
                <button
                  key={category}
                  className={`category-tab ${selectedCategory === category ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          </>
        )}
      >
        {!results || modelPerformances.length === 0 ? (
          <div className="no-data">
            <p>No completed evaluations found for this test.</p>
          </div>
        ) : (
          <div className="performance-chart">
            <h3>
              {selectedCategory === 'Overall' ? 'Overall Performance' : `Performance in ${selectedCategory}`}
            </h3>
              <div className="bar-chart">
                {/* ... (bar-chart without changes) */}
                <div className="bars-container">
                  <div className="percentage-grid">
                    <div className="grid-line" style={{ left: '0%' }}></div>
                    <div className="grid-line" style={{ left: '20%' }}></div>
                    <div className="grid-line" style={{ left: '40%' }}></div>
                    <div className="grid-line" style={{ left: '60%' }}></div>
                    <div className="grid-line" style={{ left: '80%' }}></div>
                    <div className="grid-line" style={{ left: '100%' }}></div>
                  </div>
                  <div className="percentage-labels">
                    <div className="percent-label" style={{ left: '0%' }}>0%</div>
                    <div className="percent-label" style={{ left: '20%' }}>20%</div>
                    <div className="percent-label" style={{ left: '40%' }}>40%</div>
                    <div className="percent-label" style={{ left: '60%' }}>60%</div>
                    <div className="percent-label" style={{ left: '80%' }}>80%</div>
                    <div className="percent-label" style={{ left: '100%' }}>100%</div>
                  </div>
                  {modelPerformances.map(performance => (
                    <div key={performance.modelName} className="chart-bar-container">
                      <div className="model-name">{performance.modelName}</div>
                      <div className="bar-wrapper">
                        <div
                          className="bar"
                          style={{
                            width: `${performance.score}%`,
                            backgroundColor: performance.color || '#cccccc',
                          }}
                        >
                          <span className="bar-label">{performance.score.toFixed(1)}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="performance-table-container">
                <table className="performance-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Model</th>
                      <th>Score</th>
                      <th>Evaluation Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modelPerformances.map(performance => (
                      <tr key={performance.modelName}>
                        <td>{performance.rank}</td>
                        <td>
                          <div className="model-name-with-color">
                            <span
                              className="color-indicator"
                              style={{ backgroundColor: performance.color || '#cccccc' }}
                            ></span>
                            {performance.modelName}
                          </div>
                        </td>
                        <td className="score-cell">{performance.score.toFixed(1)}%</td>
                        <td>{performance.evaluationDate.toLocaleDateString()}</td>
                        <td>
                          <Link
                            to={`/results/evaluation/${performance.evaluationId}`}
                            className={`button small ${!isAuthenticated ? 'disabled' : ''}`}
                            onClick={(e) => {
                              if (!isAuthenticated) e.preventDefault();
                            }}
                            title={!isAuthenticated ? 'Available for logged in users only' : 'View evaluation details'}
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
          </div>
        )}
      </PageLayout>
    </PublicLayout>
  );
};

export default ResultsPage; 